/**
 * DebugSessionChat — a debug session (a workflow step, the prompt builder, an entity assistant) as a conversation
 * (conversation-members spec, owner 2026-10-29: the step-debug chat moves onto the unified components). The engine
 * writes each turn of the session to a thread and names it on the session's stream; this renders that thread with
 * `ConversationChat`, exactly as any conversation renders. What only a run has stays here: starting it, following up
 * through its run (`onSend`), pause / resume / stop, reset, and handing the answer to the caller (`onResult`).
 * Demo page: /chats (an open chat with its composer).
 */
import { useQueryClient } from '@tanstack/react-query';
import { Pause, Play, RotateCcw, Square, X, Zap } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { listConversationMessagesQueryKey } from '../../generated/api/@tanstack/react-query.gen.js';
import type { ExecuteParams, StepSession } from '../../hooks/useMultiStepAgentStream.js';
import { extractJsonFromResponse } from '../../utils/contentParser.js';
import { Badge } from '../ui/Badge.js';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { ConversationChat } from './ConversationChat.js';

/** What the caller's run control does for this session. */
export interface DebugSessionActions {
  execute: (params: ExecuteParams) => Promise<void>;
  sendFollowUp: (message: string) => Promise<void>;
  stop: () => Promise<void>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  reset: () => void;
}

interface DebugSessionChatProps {
  session: StepSession | null;
  actions: DebugSessionActions;
  executeParams?: ExecuteParams;
  /** What the run will be asked, shown before it starts (the step's rendered prompt). */
  promptPreviewText?: string;
  /** The input a run started without a typed message is asked with. */
  resolvedUserPrompt?: string;
  /** A suggested first message, offered as a button before the run starts. */
  defaultInput?: string;
  /** What the run is asked for a typed first message, when the surface frames it (the source it is about). */
  inputFor?: (message: string) => unknown;
  onResult?: (result: unknown) => void;
  /** The page's focus mode follows the session: on when it starts, off when it is reset. */
  onFocusToggle?: (focused: boolean) => void;
}

const STATUS_VARIANT: Record<string, 'success' | 'danger' | 'info'> = {
  complete: 'success',
  error: 'danger',
  stopped: 'danger',
};

export function DebugSessionChat({
  session,
  actions,
  executeParams,
  promptPreviewText,
  resolvedUserPrompt,
  defaultInput,
  inputFor,
  onResult,
  onFocusToggle,
}: DebugSessionChatProps) {
  const queryClient = useQueryClient();
  const [dismissedError, setDismissedError] = useState<string | null>(null);
  const threadId = session?.threadConversationId ?? null;
  const running = session?.running ?? false;
  const paused = session?.paused ?? false;
  const status = session?.status ?? null;

  // Each finished answer goes to the caller once (a form to fill, a prompt to apply).
  const delivered = useRef<string | null>(null);
  useEffect(() => {
    if (status !== 'complete' || !session?.result) return;
    const key = `${session.currentRunId ?? ''}:${session.result}`;
    if (delivered.current === key) return;
    delivered.current = key;
    onResult?.(extractJsonFromResponse(session.result) ?? session.result);
  }, [status, session?.result, session?.currentRunId, onResult]);

  // When a turn ends, the thread is read again: the persona runtime writes its rows only then.
  useEffect(() => {
    if (threadId === null || running) return;
    void queryClient.invalidateQueries({
      queryKey: listConversationMessagesQueryKey({ path: { id: String(threadId) } }),
    });
  }, [threadId, running, queryClient]);

  const start = (message: string) => {
    if (!executeParams?.templateSlug) return;
    onFocusToggle?.(true);
    const typed = message.trim();
    const input = typed
      ? (inputFor?.(typed) ?? typed)
      : resolvedUserPrompt || executeParams.input || executeParams.config;
    return actions.execute({ ...executeParams, input });
  };

  const reset = () => {
    actions.reset();
    onFocusToggle?.(false);
  };

  const error = session?.error && session.error !== dismissedError ? session.error : null;
  const empty = (
    <div className="flex h-full flex-col justify-center gap-3 p-2">
      {promptPreviewText ? (
        <div>
          <span className="mb-1 block text-xs font-medium text-muted">What the run is asked</span>
          <pre className="max-h-[50vh] overflow-auto rounded-md border bg-surface-alt px-3 py-2 font-mono text-xs whitespace-pre-wrap text-body">
            {promptPreviewText}
          </pre>
        </div>
      ) : (
        <p className="text-center text-sm text-muted">Describe what this should do and press Enter to start.</p>
      )}
      <div className="flex flex-wrap gap-2">
        {(promptPreviewText || resolvedUserPrompt) && (
          <Button variant="success" onClick={() => void start('')} disabled={!executeParams?.templateSlug}>
            <Zap size={14} aria-hidden /> Execute
          </Button>
        )}
        {defaultInput && (
          <Button variant="secondary" onClick={() => void start(defaultInput)} disabled={!executeParams?.templateSlug}>
            {defaultInput}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      {session && (status !== null || running) && (
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {status && (
            <Badge variant={STATUS_VARIANT[status] ?? 'info'}>
              {status}
              {paused ? ' (paused)' : ''}
            </Badge>
          )}
          {session.model && <span className="text-xs text-muted">{session.model}</span>}
          <span className="flex-1" />
          {running &&
            (paused ? (
              <Button variant="success" size="sm" onClick={() => void actions.resume()}>
                <Play size={12} aria-hidden /> Resume
              </Button>
            ) : (
              <Button variant="secondary" size="sm" onClick={() => void actions.pause()}>
                <Pause size={12} aria-hidden /> Pause
              </Button>
            ))}
          {running && (
            <Button variant="danger" size="sm" onClick={() => void actions.stop()}>
              <Square size={12} aria-hidden /> Stop
            </Button>
          )}
          {!running && (
            <Button variant="ghost" size="sm" onClick={reset}>
              <RotateCcw size={12} aria-hidden /> Reset
            </Button>
          )}
        </div>
      )}
      {error && (
        <Callout tone="danger" title="The run failed">
          <div className="flex items-start justify-between gap-2">
            <pre className="text-xs whitespace-pre-wrap">{error}</pre>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Dismiss error"
              onClick={() => setDismissedError(error)}
            >
              <X size={14} aria-hidden />
            </Button>
          </div>
        </Callout>
      )}
      <ConversationChat
        key={threadId ?? 'new'}
        conversationId={threadId === null ? null : String(threadId)}
        surface="detailed"
        assistant
        onStart={(message) => void start(message)}
        starting={running && threadId === null}
        onSend={actions.sendFollowUp}
        sending={running}
        empty={empty}
        placeholder={threadId === null ? 'Describe what this should do…' : 'Send a follow-up…'}
        className="min-h-0 flex-1"
      />
    </div>
  );
}
